<!-- ServicesSection.vue (FULL UPDATED)
✅ Fetches services from API: /api/v1/consultation-services
✅ Uses Pinia store: useConsultationServicesStore
✅ Keeps your i18n/lang observer logic
✅ Best-practice UX: loading skeleton, error + retry, empty state
✅ Uses API localized fields name/description (ar/en)
✅ Read more routes to /service-details/:id
-->

<template>
  <section
    id="services"
    class="relative py-20 bg-gradient-to-b from-slate-50 to-white dark:from-slate-800 dark:to-slate-900"
  >
    <div class="container relative">
      <!-- Heading -->
      <div class="mb-12 text-center">
        <h6
          class="mb-2 text-sm font-semibold tracking-widest uppercase text-primary"
        >
          {{ t("svcs_tag") }}
        </h6>

        <h3
          class="text-3xl font-bold leading-normal text-slate-900 dark:text-white"
        >
          {{ t("svcs_h") }}
        </h3>

        <p class="max-w-2xl mx-auto mt-3 text-slate-600 dark:text-slate-400">
          {{ t("svcs_p") }}
        </p>
      </div>

      <!-- Error -->
      <div
        v-if="store.error"
        class="max-w-3xl mx-auto mb-8 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-rose-800 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200"
        role="alert"
      >
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="text-sm font-semibold">
            {{ store.error }}
          </div>

          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-white px-4 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-50 dark:border-rose-900/40 dark:bg-slate-900 dark:text-rose-200 dark:hover:bg-rose-950/30"
            @click="reload"
          >
            <i class="mdi mdi-refresh text-base"></i>
            {{ t("retry") }}
          </button>
        </div>
      </div>

      <!-- Cards -->
      <div
        class="grid items-stretch max-w-6xl grid-cols-1 gap-6 mx-auto sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
      >
        <!-- Loading skeletons -->
        <template v-if="isLoading">
          <div
            v-for="i in skeletonCount"
            :key="'svc-skel-' + i"
            class="relative flex flex-col h-full overflow-hidden border shadow-sm p-7 rounded-2xl bg-slate-50/60 dark:bg-slate-800/50 border-slate-200 dark:border-slate-600"
            aria-hidden="true"
          >
            <div
              class="w-14 h-14 rounded-full bg-slate-200/70 dark:bg-slate-700/50 animate-pulse"
            />
            <div
              class="h-4 w-2/3 mt-5 rounded bg-slate-200/70 dark:bg-slate-700/50 animate-pulse"
            />
            <div
              class="h-3 w-full mt-3 rounded bg-slate-200/60 dark:bg-slate-700/40 animate-pulse"
            />
            <div
              class="h-3 w-5/6 mt-2 rounded bg-slate-200/60 dark:bg-slate-700/40 animate-pulse"
            />
            <div
              class="h-3 w-2/3 mt-2 rounded bg-slate-200/60 dark:bg-slate-700/40 animate-pulse"
            />
            <div class="mt-auto pt-6">
              <div
                class="h-3 w-24 rounded bg-slate-200/70 dark:bg-slate-700/50 animate-pulse"
              />
            </div>
          </div>
        </template>

        <!-- Empty state -->
        <template v-else-if="!servicesCards.length && !store.error">
          <div
            class="col-span-full max-w-3xl mx-auto rounded-2xl border border-slate-200 bg-white px-6 py-10 text-center text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
          >
            {{ t("svcs_empty") }}
          </div>
        </template>

        <!-- Real cards -->
        <template v-else>
          <div
            v-for="svc in servicesCards"
            :key="'svc-' + svc.id"
            class="relative flex flex-col h-full overflow-hidden transition-all duration-500 border shadow-sm group p-7 rounded-2xl hover:-translate-y-2 bg-slate-50/60 dark:bg-slate-800/50 border-slate-200 dark:border-slate-600 hover:shadow-lg"
          >
            <div
              class="flex items-center justify-center rounded-full w-14 h-14 bg-secondary/40 text-primary"
            >
              <i :class="svc.mdi" class="text-2xl"></i>
            </div>

            <h4
              class="mt-3 text-lg font-semibold text-slate-900 dark:text-white group-hover:text-primary"
            >
              {{ svcTitle(svc) }}
            </h4>

            <!-- ✅ short preview -->
            <p
              class="mt-2 text-sm text-slate-600 dark:text-slate-400 line-clamp-3"
            >
              {{ svcDesc(svc) }}
            </p>

            <!-- Read more -->
            <div class="pt-4 mt-auto">
              <router-link
                :to="`/service-details/${svc.id}`"
                class="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
              >
                {{ t("svcs_read_more") }}
                <i :class="chevClass" class="text-[16px]"></i>
              </router-link>
            </div>

            <!-- Background icon -->
            <i
              :class="[
                svc.mdi,
                'absolute bottom-0 -end-16 text-[120px] md:text-[140px] lg:text-[160px] text-primary/10 dark:text-primary/10 pointer-events-none transition-opacity duration-500 group-hover:opacity-100 opacity-40',
              ]"
            ></i>
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<script>
import { messages } from "@/i18n";
import { useConsultationServicesStore } from "@/stores/consultationServicesStore";

export default {
  name: "ServicesSection",

  data() {
    const initialLang = (
      document.documentElement.lang ||
      localStorage.getItem("app_lang") ||
      "ar"
    ).toLowerCase();

    return {
      messages,
      lang: initialLang.startsWith("en") ? "en" : "ar",
      _langObserver: null,
      _langHandler: null,

      // Icons mapping (stable UI)
      iconsSchema: [
        { mdi: "mdi mdi-account-tie" },
        { mdi: "mdi mdi-scale-balance" },
        { mdi: "mdi mdi-bullhorn" },
        { mdi: "mdi mdi-finance" },
        { mdi: "mdi mdi-school-outline" },
        { mdi: "mdi mdi-palette" },
        { mdi: "mdi mdi-domain" },
        { mdi: "mdi mdi-lightbulb-outline" },
      ],

      skeletonCount: 8,
    };
  },

  computed: {
    store() {
      return useConsultationServicesStore();
    },

    isLoading() {
      return this.store.loadingList && !this.store.items?.length;
    },

    servicesCards() {
      const list = Array.isArray(this.store.items) ? this.store.items : [];
      const max = this.iconsSchema.length;

      // map icons by index (or change to id mapping later if needed)
      return list.slice(0, max).map((svc, i) => ({
        ...svc,
        mdi: this.iconsSchema[i]?.mdi || "mdi mdi-briefcase-outline",
      }));
    },

    chevClass() {
      const dir =
        document.documentElement.dir || (this.lang === "ar" ? "rtl" : "ltr");
      return dir === "rtl" ? "mdi mdi-chevron-left" : "mdi mdi-chevron-right";
    },
  },

  async mounted() {
    // ✅ load data once (cached in store with TTL)
    await this.store.fetchAll();

    // ✅ Immediate & reliable: respond to navbar broadcast
    this._langHandler = (e) => {
      const next = (e.detail || "ar").toLowerCase().startsWith("en")
        ? "en"
        : "ar";
      if (next !== this.lang) this.lang = next;
    };
    window.addEventListener("app:lang", this._langHandler, { passive: true });

    // Fallback: also watch <html lang/dir>
    this._langObserver = new MutationObserver((muts) => {
      for (const m of muts) {
        if (
          m.type === "attributes" &&
          (m.attributeName === "lang" || m.attributeName === "dir")
        ) {
          const htmlLang = (
            document.documentElement.lang || "ar"
          ).toLowerCase();
          const next = htmlLang.startsWith("en") ? "en" : "ar";
          if (next !== this.lang) this.lang = next;
        }
      }
    });

    this._langObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang", "dir"],
    });
  },

  beforeUnmount() {
    if (this._langObserver) this._langObserver.disconnect();
    if (this._langHandler)
      window.removeEventListener("app:lang", this._langHandler);
  },

  methods: {
    t(key) {
      return (this.messages[this.lang] && this.messages[this.lang][key]) || key;
    },

    // localized title from API: name.{ar,en}
    svcTitle(svc) {
      const name = svc?.name;
      if (!name || typeof name !== "object") return "—";
      return name[this.lang] ?? name.ar ?? name.en ?? "—";
    },

    // localized desc from API: description.{ar,en}
    svcDesc(svc) {
      const desc = svc?.description;
      if (!desc || typeof desc !== "object") return "—";
      return desc[this.lang] ?? desc.ar ?? desc.en ?? "—";
    },

    async reload() {
      await this.store.fetchAll({ force: true, ttlMs: 0 });
    },
  },
};
</script>
